// src/app/layout/drawer.ts - UPDATED

import { 
  Component, 
  inject, 
  ViewChild, 
  ViewContainerRef, 
  AfterViewInit, 
  OnDestroy,
  effect,
  DestroyRef
} from '@angular/core';

import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { DrawerService, DrawerContentConfig } from '@core/services/drawer.service';

@Component({
  selector: 'app-drawer',
  standalone: true,
  imports: [
    DrawerModule,
    ButtonModule,
    DividerModule
],
  templateUrl: './drawer.html',
  styleUrls: ['./drawer.scss']
})
export class DrawerComponent implements AfterViewInit, OnDestroy {
  private drawerService = inject(DrawerService);
  private destroyRef = inject(DestroyRef);

  @ViewChild('dynamicComponentContainer', { read: ViewContainerRef }) 
  dynamicComponentContainer!: ViewContainerRef;

  // Expose service signals for template
  isVisible = this.drawerService.isVisible;
  config = this.drawerService.config;
  isInitialized = this.drawerService.isInitialized;

  private renderEffect = effect(() => {
    const currentConfig = this.config();
    
    if (currentConfig && this.dynamicComponentContainer) {
      this.renderContent(currentConfig);
    } else if (!currentConfig && this.dynamicComponentContainer) {
      // Clear container when no config
      this.dynamicComponentContainer.clear();
    }
  });

  ngAfterViewInit(): void {
    // Initialize service with ViewContainerRef
    if (this.dynamicComponentContainer) {
      this.drawerService.setViewContainerRef(this.dynamicComponentContainer);
    }
  }

  ngOnDestroy(): void {
    // Service cleanup handled automatically by effect
    this.drawerService.close();
  }

  /**
   * Handle drawer hide event from PrimeNG
   */
  onDrawerHide(): void {
    this.drawerService.close();
  }

  /**
   * Manual close method
   */
  closeDrawer(): void {
    this.drawerService.close();
  }

  /**
   * Execute action from config
   */
  executeAction(action: any): void {
    try {
      action.handler();
    } catch (error) {
      console.error('Error executing drawer action:', error);
    }
  }

  /**
   * Render dynamic content based on configuration
   * This runs in effect when config changes
   */
  private renderContent(config: DrawerContentConfig): void {
    if (!this.dynamicComponentContainer) {
      console.warn('ViewContainerRef not available for rendering');
      return;
    }

    try {
      // Clear existing content
      this.dynamicComponentContainer.clear();

      if (config.component) {
        // Create Angular component
        const componentRef = this.dynamicComponentContainer.createComponent(config.component);
        
        // Pass data to component instance
        if (componentRef.instance && config.data) {
          // Support both @Input() data and direct property assignment
          if ('data' in componentRef.instance) {
            componentRef.instance.data = config.data;
          } else {
            // Assign properties directly
            Object.assign(componentRef.instance, config.data);
          }
        }
        
        // Trigger change detection
        componentRef.changeDetectorRef.detectChanges();
        
      } else if (config.template) {
        // Create embedded view from template
        const embeddedView = this.dynamicComponentContainer.createEmbeddedView(
          config.template, 
          { $implicit: config.data, data: config.data }
        );
        
        embeddedView.detectChanges();
      }
      
    } catch (error) {
      console.error('Error rendering drawer content:', error);
      this.renderErrorState(error);
    }
  }

  /**
   * Render error state when content creation fails
   */
  private renderErrorState(error: any): void {
    try {
      this.dynamicComponentContainer.clear();
      
      // Create simple error display
      const errorDiv = document.createElement('div');
      errorDiv.className = 'drawer-error p-4 text-center';
      errorDiv.innerHTML = `
        <div class="text-red-500 mb-2">
          <i class="pi pi-exclamation-triangle text-2xl"></i>
        </div>
        <h4 class="text-lg font-semibold mb-2">Błąd ładowania zawartości</h4>
        <p class="text-sm text-600">Spróbuj odświeżyć stronę lub skontaktuj się z administratorem.</p>
        <button class="p-button p-button-sm p-button-secondary mt-3" onclick="this.closest('p-drawer').querySelector('.p-drawer-close-icon')?.click()">
          Zamknij
        </button>
      `;
      
      // This is a fallback - in production you might want to use a proper error component
      console.warn('Rendering fallback error state');
      
    } catch (fallbackError) {
      console.error('Failed to render error state:', fallbackError);
    }
  }

  /**
   * Get dynamic styles for drawer based on config
   */
  getDrawerStyles(): any {
    const config = this.config();
    if (!config) return {};

    return {
      width: config.width || '400px'
    };
  }

  /**
   * Check if actions should be shown in footer
   */
  hasActions(): boolean {
    const config = this.config();
    return !!(config?.actions && config.actions.length > 0);
  }
}